import React, { useCallback, useEffect, useState } from "react";
import { Users, UserCog, UserPlus, ShieldCheck, Clock, ClipboardList, FileCheck, RefreshCw, Trash2, KeyRound, Ban, CheckCircle2, ChevronDown, Loader2, Circle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { PageHeader, StatCard, EmptyState, LoadingBlock } from "../../components/app/ui";
import { useAuth } from "../../context/AuthContext";
import { adminApi, formatDate, errorMessage } from "../../lib/api";

const fmtHours = (sec = 0) => {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const ROLE_LABEL = { manager: "Manager", agent: "Internal Agent" };

export default function Team() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "agent" });
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [sessions, setSessions] = useState({});
  const [pwFor, setPwFor] = useState(null);
  const [newPw, setNewPw] = useState("");

  const load = useCallback(() => {
    adminApi.staff().then(setData).catch((e) => { setData({ staff: [], counts: {} }); toast.error(errorMessage(e)); });
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);

  const create = async () => {
    if (!form.name.trim() || !form.email.trim() || form.password.length < 6) { toast.error("Enter name, email and a password of at least 6 characters"); return; }
    setBusy(true);
    try { await adminApi.createStaff(form); toast.success(`${ROLE_LABEL[form.role]} added`); setAddOpen(false); setForm({ name: "", email: "", password: "", role: "agent" }); load(); }
    catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  const patch = async (id, body, msg) => {
    try { await adminApi.updateStaff(id, body); if (msg) toast.success(msg); load(); }
    catch (e) { toast.error(errorMessage(e)); }
  };
  const remove = async (id, name) => {
    if (!window.confirm(`Remove ${name}? Their queued work will be reassigned.`)) return;
    try { await adminApi.deleteStaff(id); toast.success("Staff member removed"); load(); }
    catch (e) { toast.error(errorMessage(e)); }
  };
  const resetPw = async () => {
    if (newPw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    await patch(pwFor.user_id, { password: newPw }, "Password reset");
    setPwFor(null); setNewPw("");
  };
  const toggleSessions = async (id) => {
    if (expanded === id) { setExpanded(null); return; }
    setExpanded(id);
    if (!sessions[id]) {
      try { const r = await adminApi.staffSessions(id); setSessions((p) => ({ ...p, [id]: r.sessions || [] })); }
      catch { setSessions((p) => ({ ...p, [id]: [] })); }
    }
  };

  const counts = data?.counts || {};
  const staff = data?.staff || [];

  return (
    <>
      <PageHeader title="Team" description="Managers and internal agents who handle verifications and operational tasks." actions={
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} className="rounded-full" data-testid="team-refresh"><RefreshCw className="mr-2 h-4 w-4" /> Refresh</Button>
          {isAdmin && <Button onClick={() => setAddOpen(true)} className="rounded-full bg-brand text-white hover:bg-brand-600" data-testid="add-staff-btn"><UserPlus className="mr-2 h-4 w-4" /> Add staff</Button>}
        </div>
      } />

      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <StatCard label="Managers" value={counts.managers || 0} icon={UserCog} tone="brand" />
        <StatCard label="Internal agents" value={counts.agents || 0} icon={Users} tone="graphite" />
        <StatCard label="Online now" value={counts.online || 0} icon={ShieldCheck} tone={counts.online ? "brand" : "ink"} />
        <StatCard label="Queued tasks" value={counts.queued_tasks || 0} hint="unassigned" icon={ClipboardList} tone={counts.queued_tasks ? "brand" : "ink"} />
        <StatCard label="Queued verifications" value={counts.queued_verifications || 0} hint="unassigned" icon={FileCheck} tone={counts.queued_verifications ? "brand" : "ink"} />
      </div>

      {data === null && <LoadingBlock rows={3} />}
      {data && staff.length === 0 && <EmptyState icon={Users} title="No team members yet" text={isAdmin ? "Add a manager or internal agent to start distributing work." : "No staff have been added yet."} action={isAdmin ? <Button onClick={() => setAddOpen(true)} className="rounded-full bg-brand text-white">Add staff</Button> : null} />}

      <div className="grid gap-4" data-testid="staff-list">
        {staff.map((s) => (
          <div key={s.user_id} className="kt-card p-5" data-testid={`staff-${s.user_id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <span className={`mt-1 flex h-9 w-9 items-center justify-center rounded-full ${s.role === "manager" ? "bg-brand-50 text-brand" : "bg-surface-200 text-ink-soft"}`}>{s.role === "manager" ? <UserCog className="h-5 w-5" /> : <Users className="h-5 w-5" />}</span>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-ink">{s.name}</p>
                    <span className="inline-flex items-center gap-1 rounded-full bg-surface-100 px-2 py-0.5 text-[11px] font-semibold text-ink-soft">{ROLE_LABEL[s.role] || s.role}</span>
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${s.online ? "bg-green-100 text-green-700" : "bg-surface-200 text-ink-muted"}`} data-testid={`staff-online-${s.user_id}`}><Circle className={`h-2 w-2 ${s.online ? "fill-green-500 text-green-500" : "fill-ink-muted/40 text-ink-muted/40"}`} /> {s.online ? "Online" : "Offline"}</span>
                    {s.suspended && <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-700">Suspended</span>}
                  </div>
                  <p className="mt-1 text-sm text-ink-soft">{s.email}</p>
                  <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-ink-muted">
                    <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Today {fmtHours(s.today_seconds)} · Total {fmtHours(s.total_seconds)}</span>
                    <span className="inline-flex items-center gap-1"><ClipboardList className="h-3.5 w-3.5" /> {s.open_tasks} open tasks</span>
                    <span className="inline-flex items-center gap-1"><FileCheck className="h-3.5 w-3.5" /> {s.open_verifications} verifications</span>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => toggleSessions(s.user_id)} className="rounded-full" data-testid={`staff-hours-${s.user_id}`}><Clock className="mr-1 h-3.5 w-3.5" /> Hours <ChevronDown className={`ml-1 h-3.5 w-3.5 transition-transform ${expanded === s.user_id ? "rotate-180" : ""}`} /></Button>
                {isAdmin && (
                  <>
                    <Button variant="outline" size="sm" onClick={() => setPwFor(s)} className="rounded-full" data-testid={`staff-reset-pw-${s.user_id}`}><KeyRound className="mr-1 h-3.5 w-3.5" /> Reset password</Button>
                    <Button variant="outline" size="sm" onClick={() => patch(s.user_id, { role: s.role === "manager" ? "agent" : "manager" }, "Role updated")} className="rounded-full" data-testid={`staff-role-${s.user_id}`}>Make {s.role === "manager" ? "Agent" : "Manager"}</Button>
                    <Button variant="outline" size="sm" onClick={() => patch(s.user_id, { suspended: !s.suspended }, s.suspended ? "Reactivated" : "Suspended")} className={`rounded-full ${s.suspended ? "" : "text-amber-700"}`} data-testid={`staff-suspend-${s.user_id}`}>{s.suspended ? <><CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Reactivate</> : <><Ban className="mr-1 h-3.5 w-3.5" /> Suspend</>}</Button>
                    <Button variant="outline" size="sm" onClick={() => remove(s.user_id, s.name)} className="rounded-full border-red-200 text-red-600 hover:bg-red-50" data-testid={`staff-delete-${s.user_id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </>
                )}
              </div>
            </div>
            {expanded === s.user_id && (
              <div className="mt-4 rounded-xl bg-surface-50 p-4" data-testid={`staff-sessions-${s.user_id}`}>
                <p className="mb-2 text-sm font-semibold text-ink">Online sessions</p>
                {!sessions[s.user_id] ? <LoadingBlock rows={2} /> : sessions[s.user_id].length === 0 ? <p className="text-sm text-ink-muted">No sessions recorded yet.</p> : (
                  <ul className="divide-y divide-surface-200 text-sm">
                    {sessions[s.user_id].map((se) => (
                      <li key={se.session_id} className="flex items-center justify-between gap-3 py-1.5">
                        <span className="text-ink-soft">{formatDate(se.started_at)} {se.ongoing ? "→ now" : se.ended_at ? `→ ${formatDate(se.ended_at)}` : ""}</span>
                        <span className="shrink-0 font-medium text-ink">{se.ongoing ? "ongoing" : fmtHours(se.duration_seconds || 0)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Add staff dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-staff-dialog">
          <DialogHeader><DialogTitle>Add team member</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              {["agent", "manager"].map((r) => (
                <button key={r} type="button" onClick={() => setForm((f) => ({ ...f, role: r }))} className={`flex-1 rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors ${form.role === r ? "border-brand bg-brand-50 text-brand" : "border-surface-300 text-ink-soft hover:bg-surface-100"}`} data-testid={`role-option-${r}`}>{ROLE_LABEL[r]}</button>
              ))}
            </div>
            <p className="text-xs text-ink-muted">{form.role === "manager" ? "Managers handle verifications and can also action internal tasks." : "Internal agents handle operational tasks assigned to them."}</p>
            <div><Label htmlFor="staff-name">Full name</Label><Input id="staff-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Jane Doe" data-testid="staff-name-input" /></div>
            <div><Label htmlFor="staff-email">Email</Label><Input id="staff-email" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="jane@kohimataxi.com" data-testid="staff-email-input" /></div>
            <div><Label htmlFor="staff-pw">Temporary password</Label><Input id="staff-pw" type="text" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} placeholder="At least 6 characters" data-testid="staff-password-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={create} disabled={busy} className="rounded-full bg-brand text-white hover:bg-brand-600" data-testid="create-staff-submit">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!pwFor} onOpenChange={(o) => { if (!o) { setPwFor(null); setNewPw(""); } }}>
        <DialogContent data-testid="reset-pw-dialog">
          <DialogHeader><DialogTitle>Reset password{pwFor ? ` — ${pwFor.name}` : ""}</DialogTitle></DialogHeader>
          <div><Label htmlFor="new-pw">New password</Label><Input id="new-pw" type="text" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="At least 6 characters" data-testid="new-pw-input" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPwFor(null); setNewPw(""); }} className="rounded-full">Cancel</Button>
            <Button onClick={resetPw} className="rounded-full bg-brand text-white hover:bg-brand-600" data-testid="reset-pw-submit">Reset</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
